import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../i18n/useP';
import './StructureEditor.css';

/* Éditeur de structure générique (sections → lignes de reporting).
   Partagé par les structures P&L et Bilan. Différences passées en props :
     • renderTag(line)     — pastille avant le libellé (ex. rôle protégé P&L)
     • renderActions(line) — bouton après le libellé (ex. jeton BFRE Bilan)
     • isProtected(line)   — désactive la seule suppression (ligne porteuse d'un
        rôle ou d'un calcul) ; elle reste renommable et déplaçable.
     • isLabelLocked(line) — libellé figé (les deux lignes de résultat du bilan,
        2026-09-29) : il se lit, il ne se tape pas.
     • onRenameSection     — absent : l'entête de bloc est figée
     • virtualGroups       — [{ code, label, match(l) }] : blocs dérivés (ex. BFR)
        qui rassemblent des lignes par prédicat, hors de leur section ; les lignes
        retenues sont retirées de leur section d'origine (pas de doublon visuel).
        Pas de renommage de bloc, pas d'ajout, pas de flèches ; badge de section.
   Aucun verrou d'écran : la structure appartient au client, le serveur garde
   les seules règles métier (2026-09-03 : un drapeau `fixed` figeait tout le
   bilan, un client aux lignes en double ne pouvait plus rien retirer).
   Réordonnancement par flèches ▲ ▼ (échange voisins), renommage inline. */
export default function StructureEditor({
  title, sections, lines,
  onCreate, onRename, onDelete, onReorder, onRenameSection,
  renderTag, renderActions, renderSectionAction, isProtected, isLabelLocked,
  columns, maxCols, bare, virtualGroups, groupsOf,
}) {
  const p = useP();
  const vgroups = virtualGroups || [];

  const renderLine = (l, i, secLines) => {
    const protectedLine = !!(isProtected && isProtected(l));
    const ids = secLines.map(x => x.id);
    const move = (dir) => {
      const j = i + dir;
      if (j < 0 || j >= ids.length) return;
      const next = ids.slice();
      [next[i], next[j]] = [next[j], next[i]];
      onReorder(next);
    };
    return (
      <div key={l.id} className="fstruct-line">
        {renderTag && renderTag(l)}
        <input
          className="fstruct-input"
          defaultValue={l.label}
          readOnly={!!(isLabelLocked && isLabelLocked(l))}
          title={isLabelLocked && isLabelLocked(l) ? p('Ligne de résultat : libellé verrouillé') : undefined}
          onBlur={e => { const v = e.target.value.trim(); if (v && v !== l.label) onRename(l.id, v); }}
          onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
        />
        {renderActions && renderActions(l)}
        <button className="fstruct-ic" title={p('Monter')} disabled={i === 0} onClick={() => move(-1)}>▲</button>
        <button className="fstruct-ic" title={p('Descendre')} disabled={i === secLines.length - 1} onClick={() => move(1)}>▼</button>
        <button
          className="fstruct-ic fstruct-del"
          title={protectedLine ? p('Ligne standard — non supprimable') : p('Supprimer (les comptes seront démappés)')}
          disabled={protectedLine}
          onClick={() => {
            const msg = p('Supprimer « {{label}} » ? Les comptes affectés seront démappés.')
              .replace(/\{\{label\}\}/g, l.label);
            if (window.confirm(msg)) onDelete(l.id);
          }}
        >×</button>
      </div>
    );
  };

  const renderSection = (sec) => {
    const secLines = lines.filter(l => l.section === sec.code);
    return (
      <div key={sec.code} className="fstruct-sec">
        <div className="fstruct-sec-hwrap">
          {onRenameSection ? (
            <input
              className="fstruct-sec-h fstruct-sec-input"
              defaultValue={sec.label}
              title={p('Renommer le bloc')}
              onBlur={e => { const v = e.target.value.trim(); if (v && v !== sec.label) onRenameSection(sec.code, v); }}
              onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
            />
          ) : (
            <div className="fstruct-sec-h fstruct-sec-h--static">{sec.label}</div>
          )}
          {renderSectionAction && <div className="fstruct-sec-action">{renderSectionAction(sec)}</div>}
        </div>
        {/* Sous-totaux de GROUPE (ex. charges directes : produits vendus /
            autres) : rendus ici en LECTURE pour qu'on voie les deux niveaux
            pendant qu'on organise les lignes (demande Olivier 2026-08-16). */}
        {(groupsOf && groupsOf(sec, secLines))
          ? groupsOf(sec, secLines).map((g) => (
            <React.Fragment key={g.code}>
              <div className="fstruct-group">
                {g.lines.map((l) => renderLine(l, secLines.indexOf(l), secLines))}
              </div>
              <div className="fstruct-line fstruct-line--sub">
                <span className="fstruct-subname">{g.label}</span>
              </div>
            </React.Fragment>
          ))
          : secLines.map((l, i) => renderLine(l, i, secLines))}
        <AddLine onAdd={label => onCreate(sec.code, label)} />
      </div>
    );
  };

  // Bloc virtuel (BFR) : VUE en lecture seule. Les comptes restent dans leur
  // section Actif/Passif (avec leur jeton) ; ce bloc ne fait que récapituler
  // ceux qui sont inclus, badgés par leur section d'origine.
  const renderVirtual = (g) => {
    const gLines = lines.filter(g.match);
    return (
      <div key={g.code} className="fstruct-sec fstruct-sec--bfr">
        <div className="fstruct-sec-h fstruct-sec-h--static">{g.label}</div>
        {gLines.length === 0
          ? <div className="fstruct-vg-empty">{p("Aucun compte inclus — active le jeton BFRE sur une ligne d'actif circulant ou de dette à court terme.")}</div>
          : gLines.map((l) => (
              <div key={l.id} className="fstruct-line fstruct-line--ro">
                <span className="fstruct-secbadge">{l.section_label || l.section}</span>
                <span className="fstruct-roname">{l.label}</span>
              </div>
            ))}
      </div>
    );
  };

  // Layout en colonnes fixes (ex. Bilan : Actif | Passif) ou grille plate plafonnée.
  let body;
  if (columns) {
    const byCode = Object.fromEntries(sections.map(s => [s.code, s]));
    const vgByCode = Object.fromEntries(vgroups.map(g => [g.code, g]));
    // Colonnes virtuelles (BFRE = hors bilan) plus étroites que les colonnes bilan.
    const colStyle = columns.length === 2 ? undefined : {
      gridTemplateColumns: columns
        .map(c => (c.codes.every(code => vgByCode[code]) ? '0.85fr' : '1.2fr'))
        .join(' '),
    };
    body = (
      <div className="fstruct-cols" style={colStyle}>
        {columns.map((col, ci) => (
          <div key={ci} className="fstruct-col">
            {col.label && <div className="fstruct-col-h">{col.label}</div>}
            {col.codes.map(code => (
              vgByCode[code] ? renderVirtual(vgByCode[code])
                : byCode[code] && renderSection(byCode[code])
            ))}
          </div>
        ))}
      </div>
    );
  } else {
    body = (
      <div className={`fstruct-body${maxCols ? ` fstruct-body--cols${maxCols}` : ''}`}>
        {sections.map(renderSection)}
      </div>
    );
  }

  if (bare) return body;

  return (
    <div className="fstruct-wrap">
      {title && <div className="fstruct-head">{title}</div>}
      {body}
    </div>
  );
}

function AddLine({ onAdd }) {
  const p = useP();
  const { t } = useTranslation();
  const [val, setVal] = useState('');
  function submit() { const v = val.trim(); if (v) { onAdd(v); setVal(''); } }
  return (
    <div className="fstruct-add">
      <input
        className="fstruct-input"
        placeholder={p('+ Ajouter une ligne…')}
        value={val}
        onChange={e => setVal(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') submit(); }}
      />
      <button className="fstruct-addbtn" onClick={submit} disabled={!val.trim()}>{t('common:actions.add')}</button>
    </div>
  );
}
